import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmProvider";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, PencilSimple, NotePencil, Trash, Warning } from "@phosphor-icons/react";

const fmt = (iso) => new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

const typeMeta = {
  initial: { label: "Initial", cls: "bg-zinc-900 text-white" },
  correction: { label: "Correction", cls: "bg-primary text-white" },
  consumption: { label: "Consumption", cls: "bg-zinc-500 text-white" },
  purchase: { label: "Purchase", cls: "bg-[#16A34A] text-white" },
  "in-house": { label: "In-House Make", cls: "bg-[#0891B2] text-white" },
};

export default function MaterialDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mat, setMat] = useState(null);
  const [correctOpen, setCorrectOpen] = useState(false);
  const [newQty, setNewQty] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", category: "", unit: "", specification: "", min_stock: "" });
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("materials", "edit");
  const confirm = useConfirm();

  const load = () => api.get(`/materials/${id}`).then((r) => { setMat(r.data); setNewQty(String(r.data.current_stock)); }).catch(() => navigate("/materials"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const correct = async () => {
    if (!reason.trim()) return toast.error("Please enter a reason for the correction");
    setSaving(true);
    try {
      await api.post(`/materials/${id}/correct`, { new_quantity: Number(newQty), reason });
      toast.success("Stock corrected");
      setCorrectOpen(false);
      setReason("");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = () => {
    setEditForm({ name: mat.name, category: mat.category || "", unit: mat.unit || "", specification: mat.specification || "", min_stock: String(mat.min_stock) });
    setEditOpen(true);
  };

  const saveDetails = async () => {
    if (!editForm.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      await api.put(`/materials/${id}`, {
        name: editForm.name,
        category: editForm.category,
        unit: editForm.unit,
        specification: editForm.specification,
        min_stock: Number(editForm.min_stock) || 0,
      });
      toast.success("Details updated");
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!(await confirm({ title: "Delete Material", description: "Delete this material and its full stock history? This cannot be undone.", confirmText: "Delete" }))) return;
    try {
      await api.delete(`/materials/${id}`);
      toast.success("Material deleted");
      navigate("/materials");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  if (!mat) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  const statusCls = mat.status === "healthy" ? "bg-[#16A34A]" : mat.status === "low" ? "bg-[#EAB308]" : "bg-[#DC2626]";

  return (
    <>
      <PageHeader title={mat.name} subtitle={mat.category || "Raw Material"}>
        {canEdit && (<>
        <Button variant="outline" onClick={openEdit} data-testid="edit-material-button" className="rounded-none border-zinc-300 font-bold uppercase tracking-wider">
          <NotePencil size={16} weight="bold" className="mr-1" /> Edit Details
        </Button>
        <Button variant="outline" onClick={() => setCorrectOpen(true)} data-testid="correct-stock-button" className="rounded-none border-zinc-300 font-bold uppercase tracking-wider">
          <PencilSimple size={16} weight="bold" className="mr-1" /> Correct Stock
        </Button>
        <Button variant="outline" onClick={remove} data-testid="delete-material-button" className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white">
          <Trash size={16} weight="bold" />
        </Button>
        </>)}
      </PageHeader>

      <div className="p-4 sm:p-8 space-y-6 sm:space-y-8">
        <button onClick={() => navigate("/materials")} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900">
          <ArrowLeft size={14} weight="bold" /> All materials
        </button>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-zinc-300 p-6 md:col-span-1">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Available Stock</p>
            <p className="font-mono text-5xl font-bold text-zinc-900 mt-3 tabular">{mat.available ?? mat.current_stock}</p>
            <p className="text-sm text-zinc-400">{mat.unit}</p>
            <div className="mt-3 flex gap-4 text-xs">
              <span className="text-zinc-500">Total: <span className="font-mono text-zinc-800">{mat.current_stock}</span></span>
              <span className="text-[#EA580C]">Blocked: <span className="font-mono">{mat.reserved || 0}</span></span>
            </div>
            {(mat.blocked_by || []).length > 0 && (
              <div className="mt-3 pt-3 border-t border-zinc-100" data-testid="blocked-by-list">
                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Blocked by production</p>
                {mat.blocked_by.map((b, i) => (
                  <p key={`${b.order_no}-${i}`} className="text-xs text-[#EA580C] font-mono" data-testid={`blocked-by-${b.order_no}`}>{b.order_no} · {b.model} · {b.qty} {b.unit}</p>
                ))}
              </div>
            )}
            <span className={`inline-flex items-center gap-1 mt-4 px-2 py-1 text-[10px] font-bold uppercase text-white ${statusCls}`}>
              {mat.status !== "healthy" && <Warning size={11} weight="bold" />}{mat.status}
            </span>
          </div>
          <div className="bg-white border border-zinc-300 p-6 md:col-span-2">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">Specification</p>
            <p className="text-sm text-zinc-800 whitespace-pre-wrap min-h-[2rem]">{mat.specification || "No specification recorded."}</p>
            <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-zinc-200">
              <div><p className="text-[11px] uppercase text-zinc-400">Unit</p><p className="font-mono text-sm">{mat.unit}</p></div>
              <div><p className="text-[11px] uppercase text-zinc-400">Min Threshold</p><p className="font-mono text-sm">{mat.min_stock}</p></div>
              <div><p className="text-[11px] uppercase text-zinc-400">Added By</p><p className="text-sm">{mat.created_by}</p></div>
            </div>
          </div>
        </div>

        {/* Ledger */}
        <div className="bg-white border border-zinc-300">
          <div className="px-6 py-4 border-b border-zinc-300 bg-zinc-50">
            <h3 className="font-heading font-bold uppercase tracking-tight text-sm">Stock Ledger</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ledger-table">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-xs uppercase text-zinc-500">
                  <th className="px-6 py-3 font-bold">Type</th>
                  <th className="px-6 py-3 font-bold">Reason</th>
                  <th className="px-6 py-3 font-bold text-right">Change</th>
                  <th className="px-6 py-3 font-bold text-right">Balance</th>
                  <th className="px-6 py-3 font-bold">By / When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {mat.transactions.length === 0 && (
                  <tr><td colSpan={5} className="px-6 py-8 text-center text-zinc-400">No movements yet.</td></tr>
                )}
                {mat.transactions.map((t) => {
                  const meta = typeMeta[t.type] || { label: t.type, cls: "bg-zinc-400 text-white" };
                  return (
                    <tr key={t.id}>
                      <td className="px-6 py-3"><span className={`px-2 py-0.5 text-[10px] font-bold uppercase ${meta.cls}`}>{meta.label}</span></td>
                      <td className="px-6 py-3 text-zinc-700 max-w-xs">{t.reason}</td>
                      <td className={`px-6 py-3 text-right font-mono tabular ${t.change >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{t.change >= 0 ? "+" : ""}{t.change}</td>
                      <td className="px-6 py-3 text-right font-mono tabular font-medium">{t.resulting_stock}</td>
                      <td className="px-6 py-3 text-xs text-zinc-500">{t.by_name}<br />{fmt(t.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog open={correctOpen} onOpenChange={setCorrectOpen}>
        <DialogContent className="rounded-none border-zinc-300" data-testid="correct-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Stock Correction</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-zinc-500">Current recorded stock: <span className="font-mono font-medium text-zinc-900">{mat.current_stock} {mat.unit}</span></p>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Corrected Quantity ({mat.unit})</Label>
              <Input type="number" data-testid="correct-qty-input" value={newQty} onChange={(e) => setNewQty(e.target.value)} className="rounded-none border-zinc-300 font-mono focus-visible:ring-2 focus-visible:ring-primary" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Reason for correction *</Label>
              <Textarea data-testid="correct-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Physical count mismatch, damaged units, supplier return…" className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrectOpen(false)} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={correct} disabled={saving} data-testid="correct-save-button" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider">{saving ? "Saving…" : "Apply Correction"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-lg" data-testid="edit-material-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Edit Material Details</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Name *</Label>
              <Input data-testid="edit-name-input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider">Category</Label>
                <Input data-testid="edit-category-input" value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider">Unit</Label>
                <Input data-testid="edit-unit-input" value={editForm.unit} onChange={(e) => setEditForm({ ...editForm, unit: e.target.value })} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Specification</Label>
              <Textarea data-testid="edit-spec-input" value={editForm.specification} onChange={(e) => setEditForm({ ...editForm, specification: e.target.value })} placeholder="Grade, dimensions, material notes…" className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Minimum Threshold ({editForm.unit || mat.unit})</Label>
              <Input type="number" data-testid="edit-min-input" value={editForm.min_stock} onChange={(e) => setEditForm({ ...editForm, min_stock: e.target.value })} className="rounded-none border-zinc-300 font-mono focus-visible:ring-2 focus-visible:ring-primary" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={saveDetails} disabled={saving} data-testid="edit-save-button" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider">{saving ? "Saving…" : "Save Changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
